import { Component } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';

import { PoolFormComponent } from './pool-form/pool-form.component';
import { PoolFilterService } from '../../../core/services/pool-filter.service';
import { PracticeScopeService } from '../../../core/services/practice-scope.service';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { practiceOfCategoryKey } from '../../../core/utils/category-tree';

@Component({
  selector: 'em-pool',
  standalone: true,
  imports: [
    ButtonModule,
    ToastModule,
    DialogModule,
    DynamicDialogModule,
    ConfirmDialogModule,
    TableModule,
    TranslatePipe,
    NgFor,
    NgIf,
    EmPermissionDirective,
  ],
  templateUrl: './pool.component.html',
  styleUrl: './pool.component.scss',
})
export class PoolComponent {
  dialogRef!: DynamicDialogRef;
  translations: any = {};
  filters: any[] = [];
  Permission = Permission;

  constructor(
    private translate: TranslateService,
    private dialogService: DialogService,
    private poolFilterService: PoolFilterService,
    private practiceScope: PracticeScopeService,
  ) {}

  ngOnInit(): void {
    this.loadTranslation();
    this.loadFilters();
  }

  private loadTranslation(): void {
    this.translate
      .get([
        'layout.configurations.pool.title',
        'layout.configurations.pool.label',
        'layout.configurations.pool.addFilter',
      ])
      .subscribe((response) => {
        this.translations = response;
      });
  }

  private loadFilters(filters?: any): void {
    if (filters) {
      this.filters = this.restrictToPractice(filters);
      return;
    }

    this.poolFilterService.getPoolFilter().subscribe((response) => {
      this.filters = this.restrictToPractice(JSON.parse(response?.filterJson || '[]'));
    });
  }

  /**
   * A Manager / Manager People only sees the entries of their own practice, so
   * the pool list never shows another practice's categories. Unrestricted users
   * keep the full list.
   *
   * The practice comes from the saved entry itself: newer entries carry it, and
   * for the older ones it is decoded from the key, which holds it as well.
   */
  private restrictToPractice(filters: any[]): any[] {
    return this.practiceScope.restrict(
      filters ?? [],
      (filter) => filter?.practice ?? practiceOfCategoryKey(filter?.key),
    );
  }

  public includeForm(): void {
    this.dialogRef = this.dialogService.open(PoolFormComponent, {
      header: this.translations['layout.configurations.pool.addFilter'],
      width: '560px',
      // No fixed height. The tree is inline and scrolls internally, so the dialog
      // sizes to its content instead of the old 420px, which left a large empty
      // area under the field.
      style: { 'max-width': '95vw' },
      dismissableMask: true,
      data: { scopedPractice: this.practiceScope.getScopedPractice() },
    });

    this.dialogRef.onClose.subscribe((response) => {
      if (response?.saved && response?.filters) {
        this.loadFilters(response.filters);
      }
    });
  }
}